"use client";

import Link from "@/lib/router/link";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNotifications } from "@/lib/hooks/use-notifications";

/**
 * FR-68's "in-app via UI badge": the caller's unread count, polled.
 *
 * The badge shows ONLY what the platform said. While the count is loading, or
 * when it could not be read, there is no badge — a dot there would claim
 * something unread that nothing backs (the bell this replaces carried a
 * permanent one). The link always works: the page says what the badge cannot.
 *
 * A FAILED read is not "nothing unread" (2.689). A bare bell after a failure
 * looked exactly like a count of zero — a clean bill of health nobody issued —
 * so a failure gets its own label and a visible marker. It wins over a count
 * held from an earlier poll: that count is no longer what the platform says.
 */
export function NotificationsBell() {
  const q = useNotifications();
  const failed = q.isError;
  const unread = failed ? undefined : q.data?.unread;
  const label = failed
    ? "Notifications (unread count unavailable)"
    : unread ? `Notifications, ${unread} unread` : "Notifications";
  return (
    <Button variant="ghost" size="icon" className="relative text-muted-foreground" asChild>
      <Link href="/notifications" aria-label={label}>
        <Bell className="size-5" />
        {failed ? (
          <span
            data-testid="notifications-unavailable"
            aria-hidden
            title="The unread count could not be loaded."
            className="absolute right-1 top-1 min-w-4 rounded-full border border-muted-foreground bg-background px-1 text-center text-[10px] font-semibold leading-4 text-muted-foreground"
          >
            ?
          </span>
        ) : unread ? (
          <span
            data-testid="notifications-badge"
            className="absolute right-1 top-1 min-w-4 rounded-full bg-sev-critical px-1 text-center text-[10px] font-semibold leading-4 text-white ring-2 ring-background"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </Link>
    </Button>
  );
}
